import { useLoadObjectTypes, useObjectTypeId } from 'crm-object-board-core/objectTypeDefinition';
import { useEffect, useState, useMemo } from 'react';
import { getRegistryForObjectType } from './getRegistryForObjectType';
import { useGetHasScope } from 'crm-object-board-core/user';
import { useGetHasGate } from 'crm-object-board-core/user';
import { usePipelineId } from 'crm-object-board-core/pipeline';

// Helper function to check if user has access to a section
const hasAccessToSection = async (accessControl, hasScope, hasGate, context) => {
  var _accessControl$requir, _accessControl$requir2, _accessControl$requir3, _accessControl$requir4;
  // Default allow if no access control configured
  if (!accessControl) {
    return true;
  }

  // Default allow if access control is empty
  const hasRequiredScopes = ((_accessControl$requir = (_accessControl$requir2 = accessControl.requiredScopes) === null || _accessControl$requir2 === void 0 ? void 0 : _accessControl$requir2.length) !== null && _accessControl$requir !== void 0 ? _accessControl$requir : 0) > 0;
  const hasRequiredGates = ((_accessControl$requir3 = (_accessControl$requir4 = accessControl.requiredGates) === null || _accessControl$requir4 === void 0 ? void 0 : _accessControl$requir4.length) !== null && _accessControl$requir3 !== void 0 ? _accessControl$requir3 : 0) > 0;
  const hasCustomAccessControl = !!accessControl.customAccessControl;
  if (!hasRequiredScopes && !hasRequiredGates && !hasCustomAccessControl) {
    return true;
  }

  // Check required scopes - user must have ALL
  if (hasRequiredScopes) {
    if (!accessControl.requiredScopes.every(scope => hasScope(scope))) {
      return false;
    }
  }

  // Check required gates - user must have ALL
  if (hasRequiredGates) {
    if (!accessControl.requiredGates.every(gate => hasGate(gate))) {
      return false;
    }
  }

  // Check custom validator
  if (hasCustomAccessControl) {
    try {
      const customResult = await accessControl.customAccessControl(context);
      if (!customResult) {
        return false;
      }
    } catch (error) {
      return true;
    }
  }
  return true;
};
export const useLoadCardSectionRegistryMap = ({
  skip,
  appId,
  objectTypeId,
  hasScope,
  hasGate,
  context,
  customRegistryMap
}) => {
  const [registryMap, setRegistryMap] = useState({
    loading: true,
    data: undefined,
    error: undefined
  });
  useEffect(() => {
    if (skip) {
      setRegistryMap({
        loading: false,
        data: undefined,
        error: undefined
      });
      return;
    }
    async function getRegistryMap() {
      let map = customRegistryMap;
      if (!map) {
        const {
          registryMap: registryMapFromServer
        } = await getRegistryForObjectType(objectTypeId, appId);
        map = registryMapFromServer;
      }
      // Apply access control filtering with async support
      const accessPromises = Object.entries(map).map(async ([sectionKey, registryEntry]) => {
        var _registryEntry$card$c;
        if (!registryEntry) {
          return null;
        }

        // New generic access control check
        if (hasScope && hasGate && context && (_registryEntry$card$c = registryEntry.card.config) !== null && _registryEntry$card$c !== void 0 && _registryEntry$card$c.accessControl) {
          const hasAccess = await hasAccessToSection(registryEntry.card.config.accessControl, hasScope, hasGate, context);
          if (!hasAccess) {
            return null; // Skip this section
          }
        }

        // Include section if it passes all checks
        return {
          sectionKey,
          registryEntry
        };
      });
      const accessResults = await Promise.all(accessPromises);
      const filteredMap = {};
      accessResults.forEach(result => {
        if (result) {
          filteredMap[result.sectionKey] = result.registryEntry;
        }
      });
      setRegistryMap({
        loading: false,
        data: filteredMap,
        error: undefined
      });
    }

    // Make sure to reset the registry map to loading in cases where the skip was previously true and then later set to false
    setRegistryMap({
      loading: true,
      data: undefined,
      error: undefined
    });
    getRegistryMap().catch(e => {
      setRegistryMap({
        loading: false,
        data: undefined,
        error: e
      });
    });
  }, [objectTypeId, skip, appId, hasScope, hasGate, context, customRegistryMap]);
  return registryMap;
};
// This hook calls to getRegistryForObjectType to get the registry and configs async for the object type.
export const useCardSectionRegistryMap = ({
  skip,
  appId,
  customRegistryMap
}) => {
  const objectTypeId = useObjectTypeId();
  const pipelineId = usePipelineId();
  const {
    hasScope,
    loading: scopesLoading
  } = useGetHasScope();
  const {
    hasGate,
    loading: gatesLoading
  } = useGetHasGate();
  const {
    data: objectTypes
  } = useLoadObjectTypes();
  const objectTypeDefinition = useMemo(() => objectTypes === null || objectTypes === void 0 ? void 0 : objectTypes.find(ot => ot.objectTypeId === objectTypeId), [objectTypes, objectTypeId]);

  // Build access control context
  const context = useMemo(() => {
    // Don't create context until we have all required data
    if (!objectTypeDefinition || !hasScope || !hasGate) {
      return undefined;
    }
    return {
      objectTypeId,
      pipelineId,
      hasScope,
      hasGate,
      objectTypeDefinition
    };
  }, [objectTypeId, pipelineId, hasScope, hasGate, objectTypeDefinition]);
  return useLoadCardSectionRegistryMap({
    skip: skip || scopesLoading || gatesLoading || !context,
    // Wait for all data including context
    appId,
    objectTypeId,
    hasScope,
    hasGate,
    customRegistryMap,
    context: context //skip should keep this safe
  });
};